// Third party library imports
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Guid } from 'guid-typescript';
import { lastValueFrom } from 'rxjs';

// Zopup library imports
import {
    BizError,
    BooleanWebApiResponse,
    UOMCreateCommand,
    UOMDto,
    UOMDtoWebApiResponse,
    UOMListQuery,
    UOMUpdateCommand,
} from '@zopup/zop-types';
import {
    STORE_DEFAULTS,
    mergeEntity,
    extractFirstErrorMessage,
} from '@zopup/zop-shared';
import { serializeQuery } from '../shared/query-cache.util';

// Internal project imports
import { UOMService } from './uom.service';

// Per-store overrides for UOM cache (UOMs change infrequently)
const OVERRIDES: Partial<{ PAGE_CACHE_TTL_MS: number; ENTITY_CACHE_TTL_MS: number; DEFAULT_PAGE_SIZE: number }> = {
    PAGE_CACHE_TTL_MS: 120_000,    // 2 minutes for page cache
    ENTITY_CACHE_TTL_MS: 600_000,  // 10 minutes for entity cache
};

const PAGE_CACHE_TTL_MS = OVERRIDES.PAGE_CACHE_TTL_MS ?? STORE_DEFAULTS.PAGE_CACHE_TTL_MS;
const ENTITY_CACHE_TTL_MS = OVERRIDES.ENTITY_CACHE_TTL_MS ?? STORE_DEFAULTS.ENTITY_CACHE_TTL_MS;

export type UomPagedQuery = UOMListQuery;

type Entity = UOMDto;

type PageEntry = {
    ids: string[];
    totalCount: number;
    cachedAt: number;
    query: UomPagedQuery;
};

export type UomRepositoryState = {
    // Entity cache (normalized by ID)
    entities: Record<string, Entity>;
    entityMeta: Record<string, number>; // Cache timestamps

    // Page cache (by serialized query)
    pageCache: Record<string, PageEntry>;

    // Fast lookup indexes
    dimensionIndex: Record<string, string[]>; // dimension → uomIds[]
    codeIndex: Record<string, string>;         // code → uomId

    // Legacy state for backward compatibility
    selectedUom: UOMDto;
    isLoading: boolean;
    errors: BizError[];
    listQuery: UOMListQuery;
};

const initialState: UomRepositoryState = {
    entities: {},
    entityMeta: {},
    pageCache: {},
    dimensionIndex: {},
    codeIndex: {},
    selectedUom: {},
    isLoading: false,
    errors: [],
    listQuery: { isActive: true },
};

export const UOMRepositoryStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed((state) => ({
        // Computed signal: All entities as array (legacy compatibility)
        uoms: computed(() => Object.values(state.entities()) as Entity[]),

        // Computed signal: Active UOMs only
        activeUoms: computed(() =>
            Object.values(state.entities()).filter((u) => u.isActive) as Entity[]
        ),

        // Computed signal: UOMs grouped by dimension
        uomsByDimension: computed(() => {
            const index: Record<string, Entity[]> = {};
            const dimIndex = state.dimensionIndex();
            const entities = state.entities();

            for (const [dim, ids] of Object.entries(dimIndex)) {
                index[dim] = ids
                    .map((id) => entities[id])
                    .filter((uom): uom is Entity => !!uom);
            }

            return index;
        }),
    })),
    withMethods((store, uomService = inject(UOMService)) => {
        // ====================================================================
        // INTERNAL HELPERS
        // ====================================================================

        // Helper: Upsert entities into cache
        const upsert = (entities: Entity[]): void => {
            console.log('[UOMStore] Upserting entities:', entities.length);
            if (!entities.length) return;

            patchState(store, (state) => {
                let entitiesChanged = false;
                let metaChanged = false;
                let newEntities = state.entities;
                let newMeta = state.entityMeta;
                const now = Date.now();

                console.log('[UOMStore] Current entities count:', Object.keys(state.entities).length);

                for (const entity of entities) {
                    // API returns 'code' field, not 'id'
                    const entityId = entity.id || (entity as any).code;

                    if (!entityId) {
                        console.warn('[UOMStore] Entity without ID or code, skipping:', entity);
                        continue;
                    }

                    if (!entitiesChanged) {
                        newEntities = { ...newEntities };
                        entitiesChanged = true;
                    }
                    if (!metaChanged) {
                        newMeta = { ...newMeta };
                        metaChanged = true;
                    }

                    // Normalize entity to have 'id' field for consistency
                    const normalizedEntity = entity.id ? entity : { ...entity, id: (entity as any).code };

                    const existing = newEntities[entityId];
                    const merged = existing ? mergeEntity(existing, normalizedEntity) : normalizedEntity;
                    newEntities[entityId] = merged;
                    newMeta[entityId] = now;
                }

                const updates: Partial<UomRepositoryState> = {};
                if (entitiesChanged) updates.entities = newEntities;
                if (metaChanged) updates.entityMeta = newMeta;

                console.log('[UOMStore] Updates to apply:', {
                    entitiesChanged,
                    metaChanged,
                    newEntitiesCount: Object.keys(newEntities).length
                });

                return Object.keys(updates).length ? updates : {};
            });

            console.log('[UOMStore] After patchState, entities count:', Object.keys(store.entities()).length);

            // Rebuild indexes after upserting
            rebuildIndexes();
        };

        // Helper: Rebuild dimension and code indexes
        const rebuildIndexes = (): void => {
            patchState(store, (state) => {
                const entities = state.entities;
                const newDimensionIndex: Record<string, string[]> = {};
                const newCodeIndex: Record<string, string> = {};

                console.log('[UOMStore] Rebuilding indexes from entities:', Object.keys(entities).length);

                for (const [id, uom] of Object.entries(entities)) {
                    // Build dimension index (assuming UOMDto has a dimension field)
                    // Note: Adjust this if dimension is nested or has different structure
                    const dimension = (uom as any).dimension; // Type assertion needed as UOMDto might not have dimension in types yet
                    if (dimension) {
                        if (!newDimensionIndex[dimension]) {
                            newDimensionIndex[dimension] = [];
                        }
                        newDimensionIndex[dimension].push(id);
                    }

                    // Build code index - create multiple mappings for lookups
                    // Map by symbol (e.g., "KG" → id)
                    if (uom.symbol) {
                        newCodeIndex[uom.symbol.toUpperCase()] = id;
                        newCodeIndex[uom.symbol] = id;
                    }
                    // Map by name (e.g., "Kilogram" → id)
                    if (uom.name) {
                        newCodeIndex[uom.name.toUpperCase()] = id;
                        newCodeIndex[uom.name] = id;
                    }
                    // Map by id as fallback
                    if (uom.id) {
                        newCodeIndex[uom.id] = id;
                    }
                }

                console.log('[UOMStore] Built indexes:', {
                    dimensionIndexKeys: Object.keys(newDimensionIndex),
                    codeIndexKeys: Object.keys(newCodeIndex).slice(0, 10), // Show first 10
                    totalCodeMappings: Object.keys(newCodeIndex).length
                });

                return {
                    dimensionIndex: newDimensionIndex,
                    codeIndex: newCodeIndex,
                };
            });
        };

        // Helper: Resolve cached page
        const resolveCachedPage = (
            query: UomPagedQuery
        ): { rows: Entity[]; totalCount: number } | null => {
            const cacheKey = serializeQuery(query as unknown as Record<string, unknown>);
            const entry = store.pageCache()[cacheKey];

            if (!entry) return null;

            const now = Date.now();
            if (now - entry.cachedAt > PAGE_CACHE_TTL_MS) {
                invalidatePageCache((_, k) => k === cacheKey);
                return null;
            }

            const entities = store.entities();
            const rows = entry.ids.map((id) => entities[id]).filter((e): e is Entity => !!e);

            if (rows.length !== entry.ids.length) {
                invalidatePageCache((_, k) => k === cacheKey);
                return null;
            }

            return { rows, totalCount: entry.totalCount };
        };

        // Helper: Invalidate page cache. No predicate means "invalidate everything" — not
        // "invalidate nothing".
        const invalidatePageCache = (predicate?: (entry: PageEntry, key: string) => boolean): void => {
            patchState(store, (state) => {
                const entries = Object.entries(state.pageCache);
                if (!entries.length) return {};

                let changed = false;
                const next: Record<string, PageEntry> = {};

                for (const [key, entry] of entries) {
                    if (!predicate || predicate(entry, key)) {
                        changed = true;
                        continue;
                    }
                    next[key] = entry;
                }

                return changed ? { pageCache: next } : {};
            });
        };

        // Helper: Clear page cache
        const clearPageCache = (): void => {
            patchState(store, (state) =>
                Object.keys(state.pageCache).length ? { pageCache: {} } : {}
            );
        };

        // ====================================================================
        // REPOSITORY PATTERN METHODS (New)
        // ====================================================================

        // Core method: Fetch page with cache
        const fetchPage = async (
            query: UomPagedQuery,
            options?: { force?: boolean }
        ): Promise<{ rows: Entity[]; totalCount: number }> => {
            if (!options?.force) {
                const cached = resolveCachedPage(query);
                if (cached) return cached;
            }

            const cacheKey = serializeQuery(query as unknown as Record<string, unknown>);

            try {
                patchState(store, { isLoading: true });

                const response = await lastValueFrom(
                    uomService.uOMGet(
                        query.isActive,
                        query.orderBy,
                        query.isOrderByDescending,
                        query.searchText
                    )
                );

                console.log('[UOMStore] fetchPage response:', {
                    isSuccess: response.isSuccess,
                    resultLength: response.result?.length || 0,
                    hasErrors: (response.errors?.length || 0) > 0
                });

                if (!response.isSuccess) {
                    throw new Error(extractFirstErrorMessage(response, 'Failed to fetch UOMs'));
                }

                const rows = response.result || [];
                const totalCount = rows.length;

                console.log('[UOMStore] About to upsert rows:', totalCount);
                upsert(rows);

                patchState(store, (state) => ({
                    pageCache: {
                        ...state.pageCache,
                        [cacheKey]: {
                            ids: rows.map((r) => r.id || (r as any).code).filter(Boolean),
                            totalCount,
                            cachedAt: Date.now(),
                            query,
                        },
                    },
                    isLoading: false,
                }));

                return { rows, totalCount };
            } catch (error) {
                console.error('Exception in UOMRepositoryStore => fetchPage()');
                console.error(error);
                patchState(store, { isLoading: false });
                throw error;
            }
        };

        // Core method: Fetch by ID with cache
        const fetchById = async (
            id: string,
            options?: { force?: boolean }
        ): Promise<Entity | null> => {
            if (!id) return null;

            const now = Date.now();
            const meta = store.entityMeta()[id];
            const cached = store.entities()[id];

            if (!options?.force && cached && meta && now - meta < ENTITY_CACHE_TTL_MS) {
                return cached;
            }

            try {
                patchState(store, { isLoading: true });

                const response = await lastValueFrom(uomService.uOMIdGet(id));

                if (!response.isSuccess) {
                    throw new Error(extractFirstErrorMessage(response, 'Failed to fetch UOM'));
                }

                const entity = response.result;
                if (entity) {
                    upsert([entity]);
                    patchState(store, { selectedUom: entity });
                }

                patchState(store, { isLoading: false });
                return entity || null;
            } catch (error) {
                console.error('Exception in UOMRepositoryStore => fetchById()');
                console.error(error);
                patchState(store, { isLoading: false });
                throw error;
            }
        };

        // New method: Fetch by code using index
        const fetchByCode = async (
            code: string,
            options?: { force?: boolean }
        ): Promise<Entity | null> => {
            if (!code) return null;

            const id = store.codeIndex()[code];
            if (id) {
                return fetchById(id, options);
            }

            // If not in cache, fetch all and try again
            await fetchPage({ isActive: true }, options);
            const newId = store.codeIndex()[code];
            return newId ? store.entities()[newId] : null;
        };

        // New method: Fetch UOMs by dimension
        const fetchByDimension = async (
            dimension: string,
            options?: { force?: boolean }
        ): Promise<Entity[]> => {
            // First ensure we have data cached
            await fetchPage({ isActive: true }, options);

            const ids = store.dimensionIndex()[dimension] || [];
            const entities = store.entities();
            return ids.map((id) => entities[id]).filter((e): e is Entity => !!e);
        };

        // ====================================================================
        // EXISTING METHODS (Backward Compatibility)
        // ====================================================================

        // Legacy method: Get list
        const getList = async (): Promise<void> => {
            try {
                patchState(store, { isLoading: true });
                const query = store.listQuery();

                const response = await lastValueFrom(
                    uomService.uOMGet(
                        query.isActive,
                        query.orderBy,
                        query.isOrderByDescending,
                        query.searchText
                    )
                );

                if (response.isSuccess) {
                    const rows = response.result || [];
                    upsert(rows);
                    patchState(store, { isLoading: false });
                } else {
                    patchState(store, {
                        errors: response.errors,
                        isLoading: false,
                    });
                }
            } catch (error) {
                patchState(store, { isLoading: false });
                console.error('Exception in UOMRepositoryStore => getList()');
                console.error(error);
            }
        };

        // Legacy method: Get by ID
        const getById = async (uomId: string): Promise<UOMDto | undefined> => {
            try {
                patchState(store, { isLoading: true });
                const response = await lastValueFrom(uomService.uOMIdGet(uomId));

                if (response.isSuccess) {
                    const entity = response.result;
                    if (entity) {
                        upsert([entity]);
                    }
                    patchState(store, {
                        selectedUom: entity,
                        isLoading: false,
                    });
                    return entity;
                } else {
                    patchState(store, {
                        selectedUom: undefined,
                        errors: response.errors,
                        isLoading: false,
                    });
                    return undefined;
                }
            } catch (error) {
                console.error('Exception in UOMRepositoryStore => getById()');
                console.error(error);
                patchState(store, { isLoading: false });
                return undefined;
            }
        };

        // Create UOM
        const create = async (command: UOMCreateCommand): Promise<UOMDtoWebApiResponse> => {
            let response!: UOMDtoWebApiResponse;
            try {
                patchState(store, { isLoading: true });
                response = await lastValueFrom(
                    uomService.uOMPost(command, Guid.create().toString())
                );

                if (response.isSuccess) {
                    if (response.result) {
                        upsert([response.result]);
                        clearPageCache();
                    }
                    patchState(store, {
                        isLoading: false,
                        selectedUom: response.result,
                    });
                } else {
                    patchState(store, {
                        selectedUom: undefined,
                        errors: response.errors,
                        isLoading: false,
                    });
                }
            } catch (error) {
                patchState(store, { isLoading: false });
                console.error('Exception in UOMRepositoryStore => create()');
                console.error(error);
            }
            return response;
        };

        // Update existing UOM
        const update = async (command: UOMUpdateCommand): Promise<UOMDtoWebApiResponse> => {
            let response!: UOMDtoWebApiResponse;
            try {
                patchState(store, { isLoading: true });
                response = await lastValueFrom(uomService.uOMPut(command));

                if (response.isSuccess) {
                    if (response.result) {
                        upsert([response.result]);
                        clearPageCache();
                    }
                    patchState(store, {
                        isLoading: false,
                        selectedUom: response.result,
                    });
                } else {
                    patchState(store, {
                        selectedUom: undefined,
                        errors: response.errors,
                        isLoading: false,
                    });
                }
            } catch (error) {
                patchState(store, {
                    isLoading: false,
                });
                console.error('Exception in UOMRepositoryStore => update()');
                console.error(error);
            }
            return response;
        };

        // Delete UOM
        const deleteUom = async (uomId: string): Promise<BooleanWebApiResponse> => {
            try {
                patchState(store, { isLoading: true });
                const response = await lastValueFrom(uomService.uOMIdDelete(uomId));

                if (response.isSuccess) {
                    patchState(store, (state) => {
                        const newEntities = { ...state.entities };
                        const newMeta = { ...state.entityMeta };
                        delete newEntities[uomId];
                        delete newMeta[uomId];
                        return {
                            entities: newEntities,
                            entityMeta: newMeta,
                            isLoading: false,
                        };
                    });
                    clearPageCache();
                    rebuildIndexes();
                } else {
                    patchState(store, { isLoading: false });
                }

                return response;
            } catch (error) {
                patchState(store, { isLoading: false });
                console.error('Exception in UOMRepositoryStore => delete()');
                console.error(error);
                return { isSuccess: false };
            }
        };

        // ====================================================================
        // STATE MANAGEMENT METHODS
        // ====================================================================

        const onSelectUom = (uom: UOMDto): void => {
            patchState(store, { selectedUom: uom });
        };

        const updateListQuery = (query: UOMListQuery): void => {
            patchState(store, { listQuery: query });
        };

        const updateOrder = (order: 'asc' | 'desc'): void => {
            patchState(store, (state) => ({
                listQuery: { ...state.listQuery, order },
            }));
        };

        // ====================================================================
        // RETURN PUBLIC API
        // ====================================================================

        return {
            // Repository pattern methods (new)
            fetchPage,
            fetchById,
            fetchByCode,
            fetchByDimension,
            invalidatePageCache,
            clearPageCache,
            rebuildIndexes,

            // Existing methods (backward compatibility)
            getList,
            getById,
            create,
            update,
            delete: deleteUom,

            // State management
            onSelectUom,
            updateListQuery,
            updateOrder,
        };
    })
);

// Alias for backward compatibility
export const UOMStore = UOMRepositoryStore;
